/**
 * @description AuthorizeNetPaymentForm implementation
 * @param {AjaxForm} AjaxForm Base widget for extending
 * @returns {typeof AuthorizeNetPaymentForm}
 */

const STATE_NEW = 'newAddress';
const STATE_EDIT = 'editAddress';
const STATE_LIST = 'listAddress';

export default function (AjaxForm) {
    return class AuthorizeNetPaymentForm extends AjaxForm {
        prefs() {
            return {
                ...super.prefs(),
                addressAutocompleteEnabled: true,
                addressAutocompleteProvider: 'googleAutocomplete',
                opaqueDataField: 'dwfrm_creditCard_opaqueData',
                cardNumberField: 'dwfrm_creditCard_cardNumber',
                cardCodeField: 'dwfrm_creditCard_securityCode',
                expirationDateField: 'dwfrm_creditCard_expirationDate',
                paymentFields: [
                    'dwfrm_creditCard_cardOwner',
                    'dwfrm_creditCard_paymentMethod',
                    'dwfrm_creditCard_saveCard',
                    'dwfrm_creditCard_setAsDefault'
                ],
                templateRefId: 'template',
                container: 'addressBookContainer',
                selectedAddressId: 'dwfrm_billing_addressFields_addressId'
            };
        }

        init() {
            super.init();

            this.eventBus().on('force.focus.cvv', 'forceFocusOnCVV');
            this.initState();
        }

        initState() {
            const addressBook = this.config.addressBook;

            if (addressBook?.length) {
                this.selectDefaultAddress(addressBook);
                this.render(STATE_LIST, { addresses: addressBook, isShowAddNewAddress: true }, this.ref(this.prefs().container));
                return;
            }

            this.render(STATE_NEW, { isHideBackButton: true }, this.ref(this.prefs().container));
        }

        /**
         * @description calls when address checked has been changed
         */
        onChangeAddress(selectedAddress) {
            const addressID = selectedAddress?.get().dataset?.uuid;

            if (addressID) {
                this.setSelectedAddress(addressID);
                this.getById(this.prefs().selectedAddressId, (item) => item.setValue(addressID, true));
            }
        }

        forceFocusOnCVV() {
            this.eachChild(child => {
                if (child.id === this.prefs().cardCodeField) {
                    child.focus();
                    child.clearError();
                }
            });
        }

        /**
         * @description shows an empty address form to add new address.
         */
        newAddress() {
            this.render(STATE_NEW, {}, this.ref(this.prefs().container)).then(() => {
                this.getById(this.prefs().selectedAddressId, (item) => item.setValue('', true));
            });
        }

        /**
         * @description shows the list of saved addresses.
         */
        backToAddressBook() {
            var addressBook = this.config.addressBook;

            this.markAddressSelection(addressBook);
            this.render(STATE_LIST, { addresses: addressBook, isShowAddNewAddress: true }, this.ref(this.prefs().container));
        }

        /**
         * @description shows address form with populated address to edit it.
         */
        editAddress(editButton) {
            const addressID = editButton && editButton.data('uuid');

            this.setSelectedAddress(addressID);
            this.render(STATE_EDIT, {}, this.ref(this.prefs().container)).then(() => {
                this.fillAddressFields(editButton.get().dataset);
            });
        }

        /**
         * @description populate address in edit form.
         * @param {[]} dataset
         */
        fillAddressFields(dataset) {
            this.getAddressFields().forEach(item => {
                this.getById(item, (/** @type {basicInput} */addressField) => {
                    const name = addressField.getName() || '';
                    const shortName = name.split('_').pop();

                    if (shortName && shortName !== 'country') {
                        addressField.setValue(dataset[shortName] || '', true);
                    } else {
                        addressField.setValue(dataset.countryCode || addressField.initValue || '', true);
                        this.config.countryCode = addressField.getValue();
                    }
                });
            });
        }

        /**
         * @description Update form html body, after country change
         * @listens InputSelect#change
         * @param {basicInput} el - Country code field
         * @returns {void}
         */
        onCountryChange(el) {
            this.config.countryCode = el.getValue();

            this.updateFormData({ countryCode: this.config.countryCode })
                .then(() => this.updateAddressAutocomplete());
        }

        /**
         * @description Handles address autocomplete update with the current country code
         * @returns {void}
         */
        updateAddressAutocomplete() {
            if (this.prefs().addressAutocompleteEnabled) {
                this.getById(this.prefs().addressAutocompleteProvider, (autocompleteField) => {
                    autocompleteField.updateState(this.config.countryCode);
                });
            }
            // It is required in all site.
            var stateCode = document.getElementById('dwfrm_billing_addressFields_states_stateCode');
            var postalCode = document.getElementById('dwfrm_billing_addressFields_postalCode');
            if (stateCode && postalCode) {
                stateCode.value = '';
                postalCode.value = '';
            }
        }

        /**
         * @description Get address fields from prefs
         * @returns {string[]} Address fields names, separated by a comma
         */
        getAddressFields() {
            return this.prefs().addressFields;
        }

        /**
         *
         * @param {[]} addresses
         */
        selectDefaultAddress(addresses = []) {
            var noDefault = true;
            addresses.some((address) => {
                if (address.isDefault) {
                    this.setSelectedAddress(address.ID);
                    this.markAddressSelection(addresses);
                    noDefault = false;
                    return true;
                }
                return false;
            });

            if (noDefault && !addresses[0].isDefault) {
                this.setSelectedAddress(addresses[0].ID);
                this.markAddressSelection(addresses);
            }
        }

        /**
         * @description Set selected address
         * @param {string} addressID - Selected address ID
         * @returns {void}
         */
        setSelectedAddress(addressID) {
            this.selectedAddressID = addressID;
        }

        /**
         * @description Set `selected` attribute for selected address
         * @param {object} addresses - Addresses object
         * @returns {void}
         */
        markAddressSelection(addresses = []) {
            addresses.forEach(address => {
                address.selected = address.ID === this.selectedAddressID;
            });
        }

        /**
         * @description renders address form according to the chosen state.
         * @param {string} state the state of address form
         * @param {*} address an object with address to populate
         */
        render(state, data = {}, renderContainerRef = this.ref('fieldset'), strToRender = '') {
            const templateRef = this.prefs().templateRefId;
            // set state variable for use it in template rendering
            data[state] = true;

            return super.render(templateRef, data, renderContainerRef, strToRender);
        }

        /**
         * @description Handles submit Form
         * @returns {void|boolean} return bolean value if Form fields doen't valid
         */
        handleSubmit() {
            if (this.isChildrenValid() && !this.submitting) {
                this.submitting = true;
                this.showProgressBar();
                this.ref(this.prefs().submitButton).disable();
                this.ref(this.prefs().errorMessageLabel).hide();
                this.dispatchData(this.getFormFields());
            }

            return false;
        }

        /**
         * @description Handles opaqueData and if it's, empty error will be displayed
         * @param {object} response
         * @param {object} response.opaqueData Generic payment data type which is used
         * to process tokenized or encrypted payment information
         */
        handleTokenError(response) {
            if (!response || !response.opaqueData) {
                this.setError(this.prefs().addCardErrorMsg);
                this.afterSubmission();
            }
        }
    };
}
